import { noop } from "lodash";
import { useEffect, useState } from "react";
import type { DateValue } from "react-aria";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { DateTimePicker } from "@/components/ui/date-time-picker";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
} from "@/components/ui/form";
import { TypographyCaption } from "@/components/ui/typography";

type Props = {
  name: string;
  label?: string | React.ReactNode;
  autoFocus?: boolean;
  disabled?: boolean | string | React.ReactNode;
  description?: string;
  placeholder?: DateValue;
  readOnly?: boolean | string | React.ReactNode;
  format?: string;
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
  onFocus?: (value: Date) => void;
  onBlur?: () => void;
  optional?: boolean;
  clearButton?: boolean;
};

export const DatePickerInput = ({
  label,
  name,
  className,
  disabled = false,
  description,
  onFocus = noop,
  onBlur = noop,
  optional = false,
  readOnly = false,
  clearButton = false,
}: Props) => {
  const form = useFormContext();

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <DatePickerFieldContent
          field={field}
          label={label}
          className={className}
          disabled={disabled}
          description={description}
          onFocus={onFocus}
          onBlur={onBlur}
          optional={optional}
          readOnly={readOnly}
          clearButton={clearButton}
        />
      )}
    />
  );
};

const DatePickerFieldContent = ({
  field,
  label,
  className,
  disabled,
  description,
  onFocus,
  onBlur,
  optional,
  readOnly,
  clearButton,
}: {
  field: any;
  label?: string | React.ReactNode;
  className?: string;
  disabled: boolean | string | React.ReactNode;
  description?: string;
  onFocus: (value: Date) => void;
  onBlur: () => void;
  optional: boolean;
  readOnly: boolean | string | React.ReactNode;
  clearButton?: boolean;
}) => {
  const { error } = useFormField();
  const { t } = useTranslation();
  const form = useFormContext();
  const [value, setValue] = useState(field.value);

  useEffect(() => {
    setValue(field.value);
  }, [field.value]);

  const handleChange = (value: any) => {
    setValue(value);
  };

  const handleBlur = (event: any) => {
    if (value !== field.value) {
      field.onChange(value);
    }
    field.onBlur();
    onBlur();
  };

  const handleClear = () => {
    setValue(null);
    field.onChange(null);
    form.setValue(field.name, null, {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
  };

  return (
    <FormItem>
      {label && <FormLabel>{label}</FormLabel>}
      {optional && (
        <TypographyCaption className="text-muted-foreground capitalize !mt-0">
          {`(${t("optional")})`}
        </TypographyCaption>
      )}
      <FormControl>
        <DateTimePicker
          value={field.value}
          disabled={disabled}
          className={className}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={() => {
            onFocus(field.value);
          }}
          aria-invalid={error ? true : undefined}
          readOnly={readOnly}
          clearButton={clearButton}
          onClear={handleClear}
        />
      </FormControl>
      {description && <FormDescription>{description}</FormDescription>}
      <FormMessage />
    </FormItem>
  );
};
